import React from "react";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { BrainCircuit } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AiAnalyticsModule({
  // Translation
  t,
  lang,
  // Responsive
  isMobile,
  // Session
  session,
  // State
  aiAnalyticsImage,
  setAiAnalyticsImage,
  aiAnalyticsInstruction,
  setAiAnalyticsInstruction,
  aiAnalyticsResult,
  setAiAnalyticsResult,
  aiAnalyticsLoading,
  setAiAnalyticsLoading,
  aiAnalyticsEpochTimestamp,
  setAiAnalyticsEpochTimestamp,
  aiAnalyticsAnalyzing,
  setAiAnalyticsAnalyzing,
  aiAnalyticsImageSource,
  setAiAnalyticsImageSource,
  aiAnalyticsHelixStatus,
  setAiAnalyticsHelixStatus,
  aiAnalyticsHistory,
  // Settings
  aiAnalyticsCameraId,
  aiAnalyticsGeminiKeySaved,
  aiAnalyticsHelixUid,
  aiAnalyticsOrgId,
  // Custom Prompt state
  customPromptInput,
  setCustomPromptInput,
  customPromptResult,
  setCustomPromptResult,
  customPromptLoading,
  setCustomPromptLoading,
  // Functions
  loadAiAnalyticsHistory,
  // Auto-refresh (preview image)
  aiAutoRefreshEnabled,
  setAiAutoRefreshEnabled,
  aiAutoRefreshMinutes,
  setAiAutoRefreshMinutes,
  aiAutoRefreshPreset,
  setAiAutoRefreshPreset
}) {

  // Handle custom prompt submission
  const handleCustomPrompt = async () => {
    if (!customPromptInput.trim()) {
      alert(lang === 'de' ? 'Bitte geben Sie einen Prompt ein.' : 'Please enter a prompt.');
      return;
    }
    
    if (!aiAnalyticsGeminiKeySaved) {
      alert(lang === 'de' ? 'Bitte konfigurieren Sie zuerst den Gemini API-Key in den Einstellungen.' : 'Please configure the Gemini API key in Settings first.');
      return;
    }
    
    if (!aiAnalyticsImage) {
      alert(lang === 'de' ? 'Bitte laden Sie zuerst ein Bild (Latest Image oder manueller Upload).' : 'Please load an image first (Latest Image or manual upload).');
      return;
    }
    
    setCustomPromptLoading(true);
    setCustomPromptResult("");
    
    try {
      const res = await axios.post(`${API}/ai-analytics/custom-prompt`, {
        prompt: customPromptInput,
        base64_image: aiAnalyticsImage
      }, { headers: session.authHeaders() });
      
      if (res.data.success) {
        setCustomPromptResult(res.data.result);
      } else {
        setCustomPromptResult(res.data.result || 'Fehler bei der Anfrage');
      }
    } catch (e) {
      setCustomPromptResult('Fehler: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCustomPromptLoading(false);
    }
  };
  
  const handleFetchLatestImage = async () => {
    if (!aiAnalyticsCameraId) {
      alert(lang === 'de' ? 'Bitte konfigurieren Sie zuerst die Kamera ID in den Einstellungen.' : 'Please configure the Camera ID in Settings first.');
      return;
    }
    setAiAnalyticsLoading(true);
    setAiAnalyticsResult("");
    setAiAnalyticsImageSource(null);
    try {
      const res = await axios.get(`${API}/ai-analytics/latest-image`, { 
        headers: session.authHeaders(),
        timeout: 30000 // 30 second timeout
      });
      if (res.data.success && res.data.image) {
        setAiAnalyticsImage(res.data.image);
        setAiAnalyticsEpochTimestamp(res.data.epoch_timestamp || Date.now());
        setAiAnalyticsImageSource('api');
      }
    } catch (e) {
      let errorMsg = 'Error fetching image';
      
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        errorMsg = lang === 'de'
          ? 'Zeitüberschreitung: Die Kamera antwortet zu langsam. Bitte versuchen Sie es erneut.'
          : 'Timeout: Camera is responding too slowly. Please try again.';
      } else if (e.response) {
        // Server responded with error
        errorMsg = e.response.data?.detail || `Server Error: ${e.response.status}`;
      } else if (e.request) {
        // Request made but no response (network issue, CORS)
        errorMsg = lang === 'de' 
          ? 'Netzwerkfehler: Server antwortet nicht. Bitte prüfen Sie Ihre Internetverbindung oder versuchen Sie es später erneut.'
          : 'Network error: Server not responding. Please check your connection or try again later.';
      } else {
        errorMsg = e.message || 'Unknown error';
      }
      
      alert(errorMsg);
    } finally {
      setAiAnalyticsLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 1024;
          let width = img.width;
          let height = img.height;
          
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            } else {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          
          setAiAnalyticsImage(compressedDataUrl);
          setAiAnalyticsEpochTimestamp(Date.now());
          setAiAnalyticsResult("");
          setAiAnalyticsImageSource('upload');
          
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  // Persist auto-refresh settings to backend prefs
  const saveAutoRefreshSettings = async (enabled, minutes) => {
    try {
      await axios.patch(`${API}/user/settings`, {
        preferences: {
          ai_auto_refresh_enabled: !!enabled,
          ai_auto_refresh_minutes: Number(minutes) || 0,
        }
      }, { headers: session.authHeaders() });
    } catch (_) {}
  };

  const handleToggleAutoRefresh = () => {
    const newVal = !aiAutoRefreshEnabled;
    setAiAutoRefreshEnabled(newVal);
    saveAutoRefreshSettings(newVal, aiAutoRefreshMinutes);
  };

  const handleAutoRefreshMinutesChange = (val) => {
    const n = Number(val);
    setAiAutoRefreshMinutes(val === '' ? '' : (isNaN(n) ? 0 : n));
  };

  const handleAutoRefreshMinutesBlur = () => {
    const n = Number(aiAutoRefreshMinutes);
    const safe = isNaN(n) || n < 0 ? 0 : n;
    setAiAutoRefreshMinutes(safe);
    saveAutoRefreshSettings(aiAutoRefreshEnabled, safe);
  };

  const handlePresetChange = async (val) => {
    setAiAutoRefreshPreset(val);
    try {
      await axios.patch(`${API}/user/settings`, {
        preferences: { ai_auto_refresh_preset: val || "" }
      }, { headers: session.authHeaders() });
    } catch (_) {}
  };

  const handleAnalyze = async () => {
    if (!aiAnalyticsGeminiKeySaved) {
      alert(lang === 'de' ? 'Bitte konfigurieren Sie zuerst den Gemini API-Key in den Einstellungen.' : 'Please configure the Gemini API-Key in Settings first.');
      return;
    }
    if (!aiAnalyticsImage) {
      alert(lang === 'de' ? 'Bitte laden Sie zuerst ein Bild.' : 'Please load an image first.');
      return;
    }
    if (!aiAnalyticsInstruction.trim()) {
      alert(lang === 'de' ? 'Bitte geben Sie eine Instruktion ein.' : 'Please enter an instruction.');
      return;
    }
    
    setAiAnalyticsAnalyzing(true);
    setAiAnalyticsResult("");
    setAiAnalyticsHelixStatus(null);
    const currentInstruction = aiAnalyticsInstruction;
    
    try {
      const res = await axios.post(`${API}/ai-analytics/analyze`, {
        base64_image: aiAnalyticsImage,
        instruction: aiAnalyticsInstruction,
        epoch_timestamp: aiAnalyticsEpochTimestamp
      }, { headers: session.authHeaders() });
      
      if (res.data.success) {
        const result = res.data.result;
        setAiAnalyticsResult(result);
        
        // If result is "true" or "false" AND image was loaded via API -> create Helix Event
        if ((result === 'true' || result === 'false') && aiAnalyticsImageSource === 'api') {
          if (aiAnalyticsHelixUid && aiAnalyticsCameraId && aiAnalyticsEpochTimestamp && aiAnalyticsOrgId) {
            try {
              const helixRes = await axios.post(`${API}/ai-analytics/helix-event`, {
                result: result,
                instruction: currentInstruction,
                camera_id: aiAnalyticsCameraId,
                event_type_uid: aiAnalyticsHelixUid,
                org_id: aiAnalyticsOrgId,
                time_ms: aiAnalyticsEpochTimestamp,
                flagged: result === 'true'
              }, { headers: session.authHeaders() });
              
              if (helixRes.data.success) {
                setAiAnalyticsHelixStatus({
                  success: true, 
                  message: lang === 'de' ? '✅ Helix Event erfolgreich gesendet!' : '✅ Helix Event sent successfully!'
                });
              } else {
                setAiAnalyticsHelixStatus({
                  success: false, 
                  message: lang === 'de' ? `❌ Helix Event fehlgeschlagen: ${helixRes.data.message}` : `❌ Helix Event failed: ${helixRes.data.message}`
                });
              }
            } catch (helixErr) {
              setAiAnalyticsHelixStatus({
                success: false, 
                message: lang === 'de' ? `❌ Helix Event Fehler: ${helixErr.message}` : `❌ Helix Event error: ${helixErr.message}`
              });
            }
            // Clear image after Helix event (success or failure)
            setAiAnalyticsImage(null);
            setAiAnalyticsEpochTimestamp(null);
            setAiAnalyticsImageSource(null);
          }
        }
      } else {
        setAiAnalyticsResult(res.data.result || 'error');
      }
    } catch (e) {
      setAiAnalyticsResult('error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAiAnalyticsAnalyzing(false);
      setAiAnalyticsInstruction("");
      loadAiAnalyticsHistory();
    }
  };

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
      
      {/* AI Analytics Header */}
      <Card className="panel lightCard" style={{marginTop: 8}}>
        <CardContent>
          <div className="panel-title darktxt" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <BrainCircuit size={22} color="#6366f1" /> {t('ai_analytics_title')}
          </div>
          <div className="small">{t('ai_analytics_subtitle')}</div>
        </CardContent>
      </Card>

      <div style={{
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
        gap: isMobile ? '12px' : '16px'
      }}>
        
        {/* Left Column - Image & Buttons */}
        <Card className="panel lightCard">
          <CardContent>
            <div className="panel-title darktxt" style={{marginBottom: '16px', fontSize: '14px'}}>
              📸 {t('ai_analytics_image_preview')}
            </div>
            
            {/* Image Preview Area */}
            <div style={{
              width: '100%',
              height: '200px',
              backgroundColor: '#f8fafc',
              borderRadius: '8px',
              border: '2px dashed #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '16px',
              overflow: 'hidden'
            }}>
              {aiAnalyticsImage ? (
                <img 
                  src={aiAnalyticsImage} 
                  alt="AI Analysis" 
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <div style={{
                  textAlign: 'center',
                  color: '#94a3b8'
                }}>
                  <div style={{fontSize: '32px', marginBottom: '8px'}}>📷</div>
                  <div style={{fontSize: '13px'}}>{t('ai_analytics_no_image')}</div>
                </div>
              )}
            </div>

            {/* Auto-Refresh Toggle + Minutes */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 12px',
              marginBottom: '12px',
              backgroundColor: aiAutoRefreshEnabled ? '#ecfdf5' : '#f8fafc',
              border: `1px solid ${aiAutoRefreshEnabled ? '#10b98140' : '#e2e8f0'}`,
              borderRadius: '8px',
              flexWrap: 'wrap'
            }}>
              <label style={{display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', flex: '1 1 auto'}}>
                <input
                  type="checkbox"
                  checked={!!aiAutoRefreshEnabled}
                  onChange={handleToggleAutoRefresh}
                  data-testid="ai-auto-refresh-toggle"
                  style={{width: '18px', height: '18px', cursor: 'pointer'}}
                />
                <span style={{fontWeight: 500}}>🔄 {t('ai_auto_refresh_label')}</span>
              </label>
              <div style={{display: 'flex', alignItems: 'center', gap: '6px'}}>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={aiAutoRefreshMinutes}
                  onChange={(e) => handleAutoRefreshMinutesChange(e.target.value)}
                  onBlur={handleAutoRefreshMinutesBlur}
                  data-testid="ai-auto-refresh-minutes"
                  style={{
                    width: '70px',
                    padding: '6px 8px',
                    fontSize: '13px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    backgroundColor: 'white'
                  }}
                />
                <span style={{fontSize: '12px', color: '#64748b'}}>{t('ai_auto_refresh_minutes_unit')}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Button 
                variant="outline"
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  height: '48px',
                  minHeight: '48px'
                }}
                disabled={aiAnalyticsLoading}
                onClick={handleFetchLatestImage}
              >
                {aiAnalyticsLoading ? '⏳' : '📥'} {t('ai_analytics_get_last_image')}
              </Button>
              
              <label style={{
                flex: 1,
                padding: '12px 16px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                height: '48px',
                minHeight: '48px',
                boxSizing: 'border-box'
              }}>
                <input 
                  type="file" 
                  accept="image/*"
                  style={{display: 'none'}}
                  onChange={handleFileUpload}
                />
                📤 {t('ai_analytics_manual_upload')}
              </label>
            </div>
          </CardContent>
        </Card>
        
        {/* Right Column - Instruction & Result */}
        <Card className="panel lightCard">
          <CardContent>
            {/* Instruction Field */}
            <div style={{marginBottom: '20px'}}>
              <div className="panel-title darktxt" style={{marginBottom: '8px', fontSize: '14px'}}>
                💬 {t('ai_analytics_instruction')}
              </div>
              <textarea
                value={aiAnalyticsInstruction}
                onChange={(e) => {
                  if (e.target.value.length <= 200) {
                    setAiAnalyticsInstruction(e.target.value);
                  }
                }}
                placeholder={t('ai_analytics_instruction_placeholder')}
                maxLength={200}
                style={{
                  width: '100%',
                  height: '100px',
                  padding: '12px',
                  fontSize: '14px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  resize: 'none',
                  fontFamily: 'inherit',
                  backgroundColor: '#fff'
                }}
              />
              <div style={{
                textAlign: 'right',
                fontSize: '11px',
                color: aiAnalyticsInstruction.length >= 180 ? '#ef4444' : '#94a3b8',
                marginTop: '4px'
              }}>
                {aiAnalyticsInstruction.length}/200
              </div>

              {/* Auto-Refresh Preset Dropdown */}
              <div style={{marginTop: '12px'}}>
                <label style={{display: 'block', fontSize: '12px', color: '#475569', marginBottom: '4px', fontWeight: 500}}>
                  ⏱️ {t('ai_auto_refresh_preset_label')}
                </label>
                <select
                  value={aiAutoRefreshPreset || ''}
                  onChange={(e) => handlePresetChange(e.target.value)}
                  data-testid="ai-auto-refresh-preset"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    fontSize: '13px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="">— {t('ai_auto_refresh_preset_none')} —</option>
                  <option value="cat">{t('ai_preset_cat')}</option>
                  <option value="tree">{t('ai_preset_tree')}</option>
                  <option value="snow">{t('ai_preset_snow')}</option>
                  <option value="sun">{t('ai_preset_sun')}</option>
                </select>
                <div style={{fontSize: '11px', color: '#64748b', marginTop: '4px'}}>
                  {t('ai_auto_refresh_preset_hint')}
                </div>
              </div>
              
              {/* Analyze Button */}
              <Button 
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '12px',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                disabled={!aiAnalyticsImage || !aiAnalyticsInstruction || aiAnalyticsAnalyzing || !aiAnalyticsGeminiKeySaved}
                onClick={handleAnalyze}
              >
                {aiAnalyticsAnalyzing ? '⏳' : '🧠'} {t('ai_analytics_analyze')}
              </Button>
            </div>
            
            {/* Result Field */}
            <div>
              <div className="panel-title darktxt" style={{marginBottom: '8px', fontSize: '14px'}}>
                📊 {t('ai_analytics_result')}
              </div>
              <div style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                backgroundColor: aiAnalyticsResult === 'true' ? '#dcfce7' : aiAnalyticsResult === 'false' ? '#fee2e2' : aiAnalyticsResult === 'unknown' ? '#fef9c3' : '#f8fafc',
                minHeight: '50px',
                color: aiAnalyticsResult === 'true' ? '#166534' : aiAnalyticsResult === 'false' ? '#991b1b' : aiAnalyticsResult === 'unknown' ? '#854d0e' : aiAnalyticsResult ? '#1e293b' : '#94a3b8',
                fontWeight: aiAnalyticsResult ? '600' : '400',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {aiAnalyticsAnalyzing ? '⏳ ' + t('ai_analytics_analyzing') : (aiAnalyticsResult || t('ai_analytics_result_placeholder'))}
              </div>
              {aiAnalyticsEpochTimestamp && (
                <div style={{
                  textAlign: 'right',
                  fontSize: '11px',
                  color: '#94a3b8',
                  marginTop: '4px'
                }}>
                  Epoch: {aiAnalyticsEpochTimestamp}
                </div>
              )}
              
              {/* Helix Event Status */}
              {aiAnalyticsHelixStatus && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  borderRadius: '8px',
                  backgroundColor: aiAnalyticsHelixStatus.success ? '#dcfce7' : '#fee2e2',
                  border: `1px solid ${aiAnalyticsHelixStatus.success ? '#86efac' : '#fecaca'}`,
                  color: aiAnalyticsHelixStatus.success ? '#166534' : '#991b1b',
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'center'
                }}>
                  {aiAnalyticsHelixStatus.message}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Custom Prompt Section - Compact */}
      <Card className="panel lightCard" style={{marginTop: '16px'}}>
        <CardContent style={{padding: isMobile ? '12px' : '16px'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
            <div className="panel-title darktxt" style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', margin: 0}}>
              <BrainCircuit size={16} />
              💬 Custom Prompt
            </div>
            {/* Image Status - Compact */}
            <div style={{
              padding: '4px 10px',
              borderRadius: '12px',
              backgroundColor: aiAnalyticsImage ? '#dcfce7' : '#fef3c7',
              fontSize: '12px',
              fontWeight: '500',
              color: aiAnalyticsImage ? '#166534' : '#92400e'
            }}>
              {aiAnalyticsImage ? '✅ Bild geladen' : '⚠️ Kein Bild'}
            </div>
          </div>
          
          {/* Two Column Layout */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: '12px'
          }}>
            {/* Left: Input */}
            <div>
              <div style={{fontSize: '12px', fontWeight: '600', marginBottom: '6px', color: '#374151'}}>
                📝 Prompt
              </div>
              <textarea
                value={customPromptInput}
                onChange={(e) => setCustomPromptInput(e.target.value)}
                placeholder={lang === 'de' ? 'Frage zum Bild eingeben...' : 'Enter question about image...'}
                style={{
                  width: '100%',
                  height: '80px',
                  padding: '10px',
                  fontSize: '13px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  resize: 'none',
                  fontFamily: 'inherit'
                }}
                disabled={customPromptLoading || !aiAnalyticsImage}
              />
              <button
                onClick={handleCustomPrompt}
                disabled={customPromptLoading || !customPromptInput.trim() || !aiAnalyticsGeminiKeySaved || !aiAnalyticsImage}
                style={{
                  marginTop: '8px',
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '600',
                  backgroundColor: customPromptLoading || !customPromptInput.trim() || !aiAnalyticsGeminiKeySaved || !aiAnalyticsImage ? '#94a3b8' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: customPromptLoading || !customPromptInput.trim() || !aiAnalyticsGeminiKeySaved || !aiAnalyticsImage ? 'not-allowed' : 'pointer',
                  width: '100%'
                }}
              >
                {customPromptLoading ? '⏳ Analysiere...' : '🚀 Analysieren'}
              </button>
            </div>
            
            {/* Right: Output */}
            <div>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px'}}>
                <div style={{fontSize: '12px', fontWeight: '600', color: '#374151'}}>
                  💡 Antwort
                </div>
                {customPromptResult && (
                  <button
                    onClick={() => {
                      setCustomPromptInput("");
                      setCustomPromptResult("");
                    }}
                    style={{
                      padding: '2px 8px',
                      fontSize: '11px',
                      backgroundColor: 'transparent',
                      color: '#6b7280',
                      border: '1px solid #e2e8f0',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🗑️
                  </button>
                )}
              </div>
              <div style={{
                width: '100%',
                height: '110px',
                padding: '10px',
                fontSize: '13px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                backgroundColor: customPromptResult ? '#f8fafc' : '#fafafa',
                color: customPromptResult ? '#1e293b' : '#94a3b8',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: '1.5',
                overflowY: 'auto'
              }}>
                {customPromptLoading 
                  ? '⏳ Gemini denkt nach...' 
                  : (customPromptResult || 'Antwort erscheint hier...')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transaction History Table */}
      <Card className="panel lightCard" style={{marginTop: '16px'}}>
        <CardContent>
          <div className="panel-title darktxt" style={{marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px'}}>
            📋 {t('ai_analytics_history_title')}
          </div>
          <div style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch'}}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: isMobile ? '12px' : '14px',
              minWidth: isMobile ? '500px' : 'auto'
            }}>
              <thead>
                <tr style={{backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0'}}>
                  <th style={{padding: isMobile ? '8px 4px' : '12px 8px', textAlign: 'left', fontWeight: '600', color: '#475569'}}>{t('ai_analytics_history_datetime')}</th>
                  <th style={{padding: isMobile ? '8px 4px' : '12px 8px', textAlign: 'center', fontWeight: '600', color: '#475569'}}>{t('ai_analytics_history_image')}</th>
                  <th style={{padding: isMobile ? '8px 4px' : '12px 8px', textAlign: 'left', fontWeight: '600', color: '#475569'}}>{t('ai_analytics_history_instruction')}</th>
                  <th style={{padding: isMobile ? '8px 4px' : '12px 8px', textAlign: 'center', fontWeight: '600', color: '#475569'}}>{t('ai_analytics_history_result')}</th>
                </tr>
              </thead>
              <tbody>
                {aiAnalyticsHistory.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{padding: '24px', textAlign: 'center', color: '#94a3b8'}}>
                      {t('ai_analytics_history_empty')}
                    </td>
                  </tr>
                ) : (
                  aiAnalyticsHistory.map((entry, idx) => (
                    <tr key={idx} style={{borderBottom: '1px solid #e2e8f0'}}>
                      <td style={{padding: isMobile ? '8px 4px' : '10px 8px', color: '#374151', whiteSpace: 'nowrap'}}>
                        {new Date(entry.timestamp).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                        <br/>
                        <span style={{color: '#6b7280', fontSize: isMobile ? '10px' : '12px'}}>
                          {new Date(entry.timestamp).toLocaleTimeString(lang === 'de' ? 'de-DE' : 'en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit'
                          })}
                        </span>
                      </td>
                      <td style={{padding: '10px 8px', textAlign: 'center'}}>
                        {entry.image_thumbnail ? (
                          <img 
                            src={entry.image_thumbnail} 
                            alt="Thumbnail" 
                            style={{
                              width: isMobile ? '40px' : '50px',
                              height: isMobile ? '40px' : '50px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              border: '1px solid #e2e8f0'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div style={{
                          display: entry.image_thumbnail ? 'none' : 'flex',
                          width: isMobile ? '40px' : '50px',
                          height: isMobile ? '40px' : '50px',
                          backgroundColor: '#f1f5f9',
                          borderRadius: '4px',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94a3b8',
                          fontSize: isMobile ? '16px' : '20px',
                          margin: '0 auto'
                        }}>📷</div>
                      </td>
                      <td style={{padding: isMobile ? '8px 4px' : '10px 8px', color: '#374151', maxWidth: isMobile ? '150px' : '300px'}}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }} title={entry.instruction}>
                          {entry.instruction}
                        </div>
                      </td>
                      <td style={{padding: isMobile ? '8px 4px' : '10px 8px', textAlign: 'center'}}>
                        <span style={{
                          padding: isMobile ? '3px 8px' : '4px 12px',
                          borderRadius: '12px',
                          fontSize: isMobile ? '11px' : '13px',
                          fontWeight: '600',
                          backgroundColor: entry.result === 'true' ? '#dcfce7' : entry.result === 'false' ? '#fee2e2' : entry.result === 'unknown' ? '#fef9c3' : '#f1f5f9',
                          color: entry.result === 'true' ? '#166534' : entry.result === 'false' ? '#991b1b' : entry.result === 'unknown' ? '#854d0e' : '#475569'
                        }}>
                          {entry.result}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
